import { useState, useMemo, useCallback } from 'react';
import { Form, message, Tooltip } from 'antd';
import { useCanCheck } from '@/ability/helpers';
import { useUsers } from './useUsers';
import { useRoles } from '@hooks/rbac/useRoles';
import { useInviteUser } from './useInviteUser';
import { useCreateUser } from './useCreateUser';
import { useUpdateUser } from './useUpdateUser';
import StatusBadge from '@/components/ui/StatusBadge';
import Link from '@/components/ui/Link';
import { Dropdown } from 'antd';
import { MoreOutlined, CopyOutlined } from '@ant-design/icons';

const STATUS_TAB = { ALL: 'all', ACTIVE: 'active', INACTIVE: 'inactive' };

/** Format last login as "Feb 06, 2026 | 03:00 PM" */
const formatLastLogin = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return '—';
    const datePart = d.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
    const timePart = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    return `${datePart} | ${timePart}`;
};

export const useUsersPage = () => {
    const [searchText, setSearchText] = useState('');
    const [statusTab, setStatusTab] = useState(STATUS_TAB.ALL);
    const [filterRole, setFilterRole] = useState(undefined);
    const [filterStatus, setFilterStatus] = useState(undefined);
    const [filterModalOpen, setFilterModalOpen] = useState(false);
    const [inviteModalOpen, setInviteModalOpen] = useState(false);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [panelOpen, setPanelOpen] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [inviteForm] = Form.useForm();
    const [createForm] = Form.useForm();
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [editForm] = Form.useForm();

    const can = useCanCheck();
    const canView = can('saas.users.view');
    const canCreate = can('saas.users.create');
    const canInvite = can('saas.users.invite');
    const canUpdate = can('saas.users.update');
    const canDelete = can('saas.users.delete');

    const { data: usersResponse } = useUsers({ page: 1, limit: 100 });
    const { data: roles = [] } = useRoles();
    const inviteUserMutation = useInviteUser();
    const createUserMutation = useCreateUser();
    const updateUserMutation = useUpdateUser();

    const users = usersResponse?.data ?? [];
    const total = usersResponse?.total ?? 0;
    const activeCount = useMemo(() => users.filter((u) => (u.status || '').toLowerCase() === 'active').length, [users]);

    const selectedUserName = useMemo(
        () => users.find((u) => u.id === selectedUserId)?.name ?? 'User details',
        [users, selectedUserId],
    );

    const roleFilterOptions = useMemo(() => roles.map((r) => ({ value: r.id, label: r.name })), [roles]);

    const statusFilterOptions = useMemo(
        () => [
            { value: 'active', label: 'Active' },
            { value: 'suspended', label: 'Suspended' },
            { value: 'deactivated', label: 'Deactivated' },
        ],
        [],
    );

    const filteredUsers = useMemo(() => {
        let list = users;
        if (statusTab === STATUS_TAB.ACTIVE) list = list.filter((u) => (u.status || '').toLowerCase() === 'active');
        if (statusTab === STATUS_TAB.INACTIVE) list = list.filter((u) => (u.status || '').toLowerCase() !== 'active');
        if (filterRole) list = list.filter((u) => u.roles?.some((r) => r.id === filterRole));
        if (filterStatus) list = list.filter((u) => (u.status || '').toLowerCase() === filterStatus);
        if (searchText.trim()) {
            const q = searchText.toLowerCase();
            list = list.filter(
                (u) => (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q),
            );
        }
        return list;
    }, [users, statusTab, filterRole, filterStatus, searchText]);

    const tableData = useMemo(
        () =>
            filteredUsers.map((u) => ({
                key: u.id,
                id: u.id,
                name: u.name,
                email: u.email,
                status: u.status,
                roles: u.roles,
                plan: u.plan,
                lastLogin: u.lastLogin,
            })),
        [filteredUsers],
    );

    const handleInviteUser = useCallback(() => {
        inviteForm.validateFields().then((values) => {
            inviteUserMutation.mutate(
                { email: values.email, roleId: values.roleId },
                {
                    onSuccess: () => {
                        message.success('Invitation sent');
                        setInviteModalOpen(false);
                        inviteForm.resetFields();
                    },
                    onError: (err) => {
                        message.error(err?.response?.data?.message || 'Failed to invite user');
                    },
                },
            );
        });
    }, [inviteForm, inviteUserMutation]);

    const handleCreateUser = useCallback(() => {
        createForm.validateFields().then((values) => {
            createUserMutation.mutate(
                {
                    email: values.email,
                    name: values.name,
                    roleId: values.roleId,
                    password: values.password,
                },
                {
                    onSuccess: () => {
                        message.success('User created successfully');
                        setCreateModalOpen(false);
                        createForm.resetFields();
                    },
                    onError: (err) => {
                        message.error(err?.response?.data?.message || 'Failed to create user');
                    },
                },
            );
        });
    }, [createForm, createUserMutation]);

    const handleActionClick = useCallback(
        (menuInfo, record) => {
            if (menuInfo.key === 'edit') {
                const fullUser = users.find((u) => u.id === record.id) || record;
                setEditingUser(fullUser);
                editForm.setFieldsValue({
                    name: fullUser.name,
                    email: fullUser.email,
                    status: ['active', 'suspended', 'deactivated'].includes(fullUser.status) ? fullUser.status : 'active',
                    roleId: fullUser.roles?.[0]?.id,
                });
                setEditModalOpen(true);
            }
        },
        [users, editForm],
    );

    const closePanel = useCallback(() => {
        setPanelOpen(false);
        setSelectedUserId(null);
    }, []);

    const openPanel = useCallback((id) => {
        setSelectedUserId(id);
        setPanelOpen(true);
    }, []);

    const handleCopyEmail = useCallback((email, e) => {
        e?.stopPropagation?.();
        if (!email) return;
        navigator.clipboard?.writeText(email).then(
            () => message.success('Email copied'),
            () => message.error('Failed to copy'),
        );
    }, []);

    const columns = useMemo(
        () => [
            { title: 'Name', dataIndex: 'name', key: 'name' },
            {
                title: 'Email',
                dataIndex: 'email',
                key: 'email',
                render: (email) => {
                    if (!email) return '—';
                    return (
                        <span onClick={(e) => e.stopPropagation()}>
                            <Link href={`mailto:${email}`} style={{ marginRight: 8 }}>
                                {email}
                            </Link>
                            <Tooltip title="Copy email">
                                <CopyOutlined
                                    style={{ cursor: 'pointer', color: 'var(--ant-color-text-tertiary)', fontSize: 14 }}
                                    onClick={(e) => handleCopyEmail(email, e)}
                                />
                            </Tooltip>
                        </span>
                    );
                },
            },
            {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                width: 100,
                render: (_, record) => <StatusBadge status={record.status} />,
            },
            {
                title: 'Roles',
                key: 'roles',
                width: 160,
                render: (_, record) => (record.roles?.length ? record.roles.map((r) => r.name).join(', ') : '—'),
            },
            {
                title: 'Plan',
                key: 'plan',
                width: 100,
                render: (_, record) => record.plan?.name ?? '—',
            },
            {
                title: 'Last login',
                key: 'lastLogin',
                width: 200,
                render: (_, record) => formatLastLogin(record.lastLogin),
            },
            ...(canUpdate || canDelete
                ? [
                      {
                          title: 'Action',
                          key: 'action',
                          width: 80,
                          render: (_, record) => (
                              <span onClick={(e) => e.stopPropagation()}>
                                  <Dropdown
                                      menu={{
                                          items: [
                                              ...(canUpdate ? [{ key: 'edit', label: 'Edit' }] : []),
                                              // ...(canDelete ? [{ key: 'delete', label: 'Delete', danger: true }] : []),
                                          ],
                                          onClick: (info) => handleActionClick(info, record),
                                      }}
                                      trigger={['click']}
                                  >
                                      <span style={{ cursor: 'pointer' }}>
                                          <MoreOutlined />
                                      </span>
                                  </Dropdown>
                              </span>
                          ),
                      },
                  ]
                : []),
        ],
        [canUpdate, canDelete, handleActionClick, handleCopyEmail],
    );

    const activeFilterCount = [filterRole, filterStatus].filter(Boolean).length;

    const clearFilters = useCallback(() => {
        setFilterRole(undefined);
        setFilterStatus(undefined);
    }, []);

    const handleEditUser = useCallback(() => {
        editForm.validateFields().then((values) => {
            if (!editingUser?.id) return;
            updateUserMutation.mutate(
                {
                    id: editingUser.id,
                    body: {
                        status: values.status,
                        roleId: values.roleId,
                    },
                },
                {
                    onSuccess: () => {
                        setEditModalOpen(false);
                        setEditingUser(null);
                        editForm.resetFields();
                    },
                },
            );
        });
    }, [editForm, editingUser, updateUserMutation]);

    return {
        searchText,
        setSearchText,
        statusTab,
        setStatusTab,
        STATUS_TAB,
        filterRole,
        setFilterRole,
        filterStatus,
        setFilterStatus,
        filterModalOpen,
        setFilterModalOpen,
        activeFilterCount,
        clearFilters,
        roleFilterOptions,
        statusFilterOptions,
        inviteModalOpen,
        setInviteModalOpen,
        createModalOpen,
        setCreateModalOpen,
        panelOpen,
        closePanel,
        openPanel,
        selectedUserId,
        selectedUserName,
        total,
        activeCount,
        tableData,
        columns,
        users,
        inviteForm,
        createForm,
        editForm,
        handleInviteUser,
        handleCreateUser,
        handleEditUser,
        roles,
        canView,
        canCreate,
        canInvite,
        inviteUserMutation,
        createUserMutation,
        updateUserMutation,
        editModalOpen,
        setEditModalOpen,
        editingUser,
    };
};
